import { CONSTANT } from '@const';
import { Form03ExternalData, ModuleAForm01Values, ModuleAFormValues } from '@moduleAForm';
import {
	CertificateItem,
	DeviceMakeModelItem,
	MCCSummaryItem,
	UpdateCertificatePayload,
} from '@types';
import { isEmpty, isNil } from 'lodash';

import { CertificateCreationMapper } from '../mappers/CertificateCreationMapper';
import { CertificateMapper } from '../mappers/CertificateMapper';
import { CertificateService } from './CertificateService';

export class ModuleAFormService implements CertificateService {
	protected static baseUrl = CONSTANT.DQP_API_BASE_URL;

	static mapDataToModuleAForm(
		certificateData: CertificateItem,
		meterMakesModelsData: DeviceMakeModelItem[],
		sensorMakesModelsData: DeviceMakeModelItem[],
		isFormV9?: boolean,
	): ModuleAFormValues {
		const certificateMapper = new CertificateMapper(
			meterMakesModelsData,
			sensorMakesModelsData,
			isFormV9, // TODO: (uplift) remove when old form is removed
		);
		return certificateMapper.mapToForm(certificateData);
	}

	static async create(mccSummary: MCCSummaryItem, formValues: ModuleAForm01Values) {
		console.group('%cModuleAFormService - request to POST /Certificates', 'color: hotpink');
		console.log('%cForm values', 'color: cornflowerblue', formValues);
		const certificateCreationMapper = new CertificateCreationMapper(mccSummary);
		const payload = certificateCreationMapper.mapFormToPayload(formValues);
		console.log('%cMapped form values to payload', 'color: lightpink', payload);
		console.groupEnd();
		return await CertificateService.createCertificate(payload).then(data => {
			console.group(
				'%cModuleAFormService - response from POST /Certificates',
				'color: forestgreen',
			);
			console.log('%cResponse data', 'color: darkseagreen', data);
			console.groupEnd();
			return data;
		});
	}

	static async update(
		certificateId: string,
		certificate: CertificateItem,
		formValues: Partial<ModuleAFormValues>,
		meterMakesModelsData: DeviceMakeModelItem[],
		sensorMakesModelsData: DeviceMakeModelItem[],
	): Promise<CertificateItem> {
		const certificateMapper = new CertificateMapper(meterMakesModelsData, sensorMakesModelsData);
		console.group('%cRequest to PUT /Certificates/{id}', 'color: hotpink');
		console.log('%cForm values', 'color: cornflowerblue', formValues);
		const dataOut1 = certificateMapper.mapModelToPayload(certificate);
		const dataOut2 = certificateMapper.mapFormToPayload(formValues);
		const dataOut: UpdateCertificatePayload = {
			...dataOut1,
			...dataOut2,
		};
		console.log('%cMapped certificate to payload', 'color: lightpink', dataOut1);
		console.log('%cMapped form values to payload', 'color: lightpink', dataOut2);
		console.log('%cMapped certificate and form values to payload', 'color: lightpink', dataOut);
		console.groupEnd();
		return await CertificateService.updateCertificate(certificateId, dataOut);
	}

	/**
	 * Extracts the external data context for Form 03 from the provided certificate data, meter makes and models data, and sensor makes and models data.
	 *
	 * EMAccuracy visibility rules.
	 * This field is visible if:
	 * The <MDC Form: Existing Make And Model Type> has <MD Make And Model Device Type: Pattern Approved> equal to "Approved" or "Existing Approved"
	 * Or if:
	 * (The <MDC Form: Existing Make And Model Type> has <MD Make And Model Device Type: Pattern Approved> equal to "Approved" or "Existing Approved"
	 * And if:
	 * The <MDC Form: Existing Make And Model Type> has <MD Make And Model Device Type: MD Device Type> = "Modular Meter")
	 *
	 * In simple terms, this label should be visible if a) the MD's make & model type is approved/existing approved or b)
	 * the MD make and model type is approved/existing approved, is a modular meter and needs to have at least 1 sensor MD connected to it
	 *
	 * @param {CertificateItem} certificateData - The certificate data to extract the external data context from.
	 * @param {DeviceMakeModelItem[]} meterMakesModelsData - The meter makes and models data to use for extraction.
	 * @param {DeviceMakeModelItem[]} sensorMakesModelsData - The sensor makes and models data to use for extraction.
	 * @return {Form03ExternalData} The extracted external data context for Form 03.
	 */
	static extractForm03ExternalDataContext(
		certificateData: CertificateItem,
		meterMakesModelsData: DeviceMakeModelItem[],
		sensorMakesModelsData: DeviceMakeModelItem[],
	): Form03ExternalData {
		const certificateMapper = new CertificateMapper(meterMakesModelsData, sensorMakesModelsData);
		const makeModel = certificateMapper.extractMakeModelFromDevice(
			certificateData.existingDeviceDetails,
		);

		// Check out if pattern is approved on the make and model record
		const isApprovedOrExistingApproved = this.isApprovedOrExistingApproved(makeModel);

		// Accuracy visibility rules' validation
		const isValidOnAccuracyRules = this.validateAccuracyRules(makeModel, certificateData);

		// Existing device data
		const existingDeviceData = certificateMapper.mapInExistingDeviceDetails(certificateData);

		return {
			existingDeviceFormValues: existingDeviceData,
			isValidOnAccuracyRules: isValidOnAccuracyRules,
			isApprovedOrExistingApproved: isApprovedOrExistingApproved,
		};
	}

	private static isApprovedOrExistingApproved(makeModel: DeviceMakeModelItem | undefined): boolean {
		const patternApproved = makeModel?.patternApproved;
		return (
			patternApproved === DeviceMakeModelItem.patternApproved.APPROVED ||
			patternApproved === DeviceMakeModelItem.patternApproved.EXISTING_APPROVED
		);
	}

	/**
	 * Check the visibility rules for the accuracy question
	 */
	private static validateAccuracyRules(
		makeModel: DeviceMakeModelItem | undefined,
		certificateData: CertificateItem,
	): boolean {
		console.group('%c==> Accuracy visibility rules', 'color: mediumpurple');
		console.log('Meter device make model pattern:', makeModel?.patternApproved); // prettier-ignore
		console.log('Meter device type:', makeModel?.deviceType);
		let isValidOnAccuracyRules;
		switch (makeModel?.deviceType) {
			case DeviceMakeModelItem.deviceType.SC_METER:
				isValidOnAccuracyRules = this.isValidClosedConduitMeter(makeModel, certificateData);
				break;
			case DeviceMakeModelItem.deviceType.MODULAR_METER:
				isValidOnAccuracyRules = this.isValidModularMeterSystem(makeModel, certificateData);
				break;
			default:
				isValidOnAccuracyRules = false;
				break;
		}
		console.log('%cAre the accuracy visibility rules met?', 'color: mediumpurple; font-weight: bold', isValidOnAccuracyRules); // prettier-ignore
		console.groupEnd();
		return isValidOnAccuracyRules;
	}

	/**
	 * Check conditions for closed conduit meter
	 */
	private static isValidClosedConduitMeter(
		makeModel: DeviceMakeModelItem | undefined,
		certificateData: CertificateItem,
	): boolean {
		const isValidSize = !isNil(certificateData.existingDeviceDetails?.size);
		console.log('is size valid?', isValidSize);
		return this.isValidCommonDeviceConditions(makeModel, certificateData) && isValidSize;
	}

	/**
	 * Check conditions for modular metering system
	 */
	private static isValidModularMeterSystem(
		makeModel: DeviceMakeModelItem | undefined,
		certificateData: CertificateItem,
	): boolean {
		const isValidCommonDeviceConditions = this.isValidCommonDeviceConditions(
			makeModel,
			certificateData,
		);
		return isValidCommonDeviceConditions;
	}

	/**
	 * Check the validity of common conditions from the meter device
	 */
	private static isValidCommonDeviceConditions(
		makeModel: DeviceMakeModelItem | undefined,
		certificateData: CertificateItem,
	): boolean {
		const isApprovedOrExistingApproved = this.isApprovedOrExistingApproved(makeModel);
		const isValidMakeAndModelType =
			!isEmpty(certificateData.existingDeviceDetails?.makeAndModelType) &&
			certificateData.existingDeviceDetails?.makeAndModelType !== CONSTANT.INVALID_UUID;
		const isValidSerialNumber = !isEmpty(certificateData.existingDeviceDetails?.serialNumber);
		const isValidLatitude = !isNil(certificateData.existingDeviceDetails?.latitude);
		const isValidLongitude = !isNil(certificateData.existingDeviceDetails?.longitude);
		const isValidGpsDatum = !isEmpty(certificateData.existingDeviceDetails?.gpsDatum);

		const isValidCommonDeviceConditions =
			isApprovedOrExistingApproved &&
			isValidMakeAndModelType &&
			isValidSerialNumber &&
			isValidLatitude &&
			isValidLongitude &&
			isValidGpsDatum;

		console.group("Validation of meter device's common data");
		console.log('is pattern approved or existing approved?', isApprovedOrExistingApproved);
		// Debugging logs when is not valid
		if (!isValidCommonDeviceConditions) {
			console.log('make and model type', isValidMakeAndModelType);
			console.log('serial number', isValidSerialNumber);
			console.log('latitude', isValidLatitude);
			console.log('longitude', isValidLongitude);
			console.log('gps Datum', isValidGpsDatum);
		}
		console.log('%care common device conditions valid?', 'font-weight: bold', isValidCommonDeviceConditions); // prettier-ignore
		console.groupEnd();

		return isValidCommonDeviceConditions;
	}
}
